import LoadingButton from "@mui/lab/LoadingButton";
import { Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SendEmailDialog } from "../../components/dialog/EmailSender";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { getCustomers, useSendPaymentNotification } from "../../services/agreementsService";
import DateService from "../../services/dateService";
import { isFinalSettlementPayment } from "../../services/agreementsService";
import { SelectDrafNotifcationDialog } from "./SelectDraftNotificationDialog";
import { NOTIFICATION_FOR_PAYMENTS_KINDS, NotificationKindsForPaymentEnum } from "../../schemas/agreements";
import { useGenerateNotificationMailDraft } from "../../services/draftsService";
import { useRef } from "react";

export const SendPaymentNotificationButton = (props) => {
    const { agreement, payment, paymentIdx, allPayments, disabled } = props;
    const { t } = useTranslation();
    const { openDialog: openSendEmailDialog, closeDialog: closeSendEmailDialog } = useDialogBetter();
    const { openDialog: openSelectDraftDialog, closeDialog: closeSelectDraftDialog } = useDialogBetter();
    const { mutate: sendNotification, isPending } = useSendPaymentNotification();
    const { mutate: genereateDraft, isPending: isGeneratingDraft } = useGenerateNotificationMailDraft();

    const anyPaymentHasDueDate = allPayments.some((p) => p.agrepay_due_date != null);

    const lowestUnsentPayment = allPayments
        .filter((p) => !p.agrepay_notification_send_date)
        .sort((a, b) => {
            const indexA = allPayments.findIndex((p) => p.id === a.id);
            const indexB = allPayments.findIndex((p) => p.id === b.id);
            return indexA - indexB;
        })[0];
    const isThisFirstUnpaidPayment = lowestUnsentPayment?.id === payment.id;

    const isEnabled = !disabled && (anyPaymentHasDueDate || isThisFirstUnpaidPayment || payment?.agrepay_milestone || isFinalSettlementPayment(payment));

    const recipients = getCustomers(agreement)
        .map((customer) => customer.person?.email?.email_address || customer.company?.email?.email_address)
        .filter((email) => email != null);

    const today = new Date();
    const initialEmailSubject = useRef(null)
    const initialEmailBody = useRef(null)
    // Calculate initial due date if payment_shift is set
    let initialDueDate = payment.agrepay_due_date || null;
    if (payment.agrepay_payment_shift != null && !initialDueDate) {
        const dueDate = new Date(today);
        dueDate.setDate(dueDate.getDate() + payment.agrepay_payment_shift);
        initialDueDate = DateService.convertDateToFormatYYYYMMDD(dueDate);
    }

    const handleSendEmail = (subject, body, dueDate) => {
        sendNotification(
            {
                agrepay: payment.id,
                subject,
                body,
                due_date: dueDate || null,
            },
            {
                onSuccess: () => {
                    closeSendEmailDialog();
                },
            },
        );
    };

    const handleOpenEmailDialog = () => {
        const isExcessFinalSettlementPayment = isFinalSettlementPayment(payment) && payment.remaining_amount < 0
        openSendEmailDialog({
            Component: SendEmailDialog,
            componentProps: {
                recipientEmails: recipients,
                initialSubject: initialEmailSubject.current,
                initialBody: initialEmailBody.current,
                onSendEmail: handleSendEmail,
                title: t("agreements.SendNotificationButton.emailDialogTitle"),
                showDueDateField: isExcessFinalSettlementPayment ? false : true,
                initialDueDate: isExcessFinalSettlementPayment ? null : initialDueDate,
                isDueDateRequired: !isExcessFinalSettlementPayment
            },
        });
    };

    const handleSelectDraftDialog = (draftId)=> {
            genereateDraft(
            {
                draftId: draftId,
                requestBody: {
                    agreement_id: props.agreement.id,
                    agreement_payment_id: props.payment.id
                }
            },
            {
                onSuccess: (response) => {
                    initialEmailSubject.current = response?.data?.subject
                    initialEmailBody.current = response?.data?.body
                    handleOpenEmailDialog()
                },
            },
        )
    }

    const handleOpenSelectDraftDialog = () => {
        openSelectDraftDialog({
            Component: SelectDrafNotifcationDialog,
            componentProps: {
                onConfirm: handleSelectDraftDialog,
                draft_notification: 
                    isFinalSettlementPayment(payment) ? 
                        NOTIFICATION_FOR_PAYMENTS_KINDS.find((notificaiton)=>notificaiton.key === NotificationKindsForPaymentEnum.FINAL_SETTLEMENT).id : 
                        NOTIFICATION_FOR_PAYMENTS_KINDS.find((notificaiton)=>notificaiton.key === NotificationKindsForPaymentEnum.TRANCHE).id
            },
        });
    };

    if (payment.agrepay_notification_send_date) {
        return <Chip label={t("agreements.payment.sent")} color="success" size="small" />;
    }

    return (
        <LoadingButton loading={isPending} onClick={handleOpenSelectDraftDialog} disabled={!isEnabled}>
            {t("agreements.SendNotificationButton.send")}
        </LoadingButton>
    );
};
